import { mostHelpfulOptions } from "#/components/carrot-plans/most-helpful-options/mostHelpfulOptions"
import { HelpfulOption } from "#/components/carrot-plans/most-helpful-options/helpfulOption"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { RoutingInfo } from "#/types/routingInfo"
import { useIntl } from "react-intl"
import {
  CategoryOption,
  CategoryTitle,
  FlattenedOption,
  categoryTitleLabels
} from "#/components/carrot-plans/most-helpful-options/categorizedOptions"
import { useGetFilteredHelpfulOptions } from "#/components/carrot-plans/hooks/useGetFilteredHelpfulOptions"

export function useGetCategorizedMostHelpfulOptions(
  categorizedOptions: CategoryOption[],
  routingData: RoutingInfo,
  benefit: Benefit
): {
  flatCategorizedOptions: FlattenedOption[]
} {
  const intl = useIntl()

  const helpfulOptions = categorizedOptions.flatMap((category) => category.options)
  const filteredHelpfulOptions = useGetFilteredHelpfulOptions(routingData, benefit, helpfulOptions)
  const filteredCategorizedOptions = categorizedOptions
    .map((category) => ({
      ...category,
      options: category.options.filter((option) => filteredHelpfulOptions.includes(option))
    }))
    .filter((category) => category.options.length > 0)

  // Cache translations for helpful options
  const translatedHelpfulOptionsMap = new Map(
    mostHelpfulOptions
      .filter((option) => filteredHelpfulOptions.includes(option.value as HelpfulOption))
      .map((option) => [option.value, intl.formatMessage(option.label)])
  )

  // Cache translations for category titles
  const categoryTitleLabelsMap = new Map(
    categoryTitleLabels.map(({ value, label }) => [value, intl.formatMessage(label)])
  )

  // Create flattened options with category titles
  const flattenedMostHelpfulOptions = filteredCategorizedOptions.flatMap((category) => {
    const isInitialCategory = category.title === CategoryTitle.INITIAL_CATEGORY
    const translatedCategoryTitle = category.title ? categoryTitleLabelsMap.get(category.title) || null : null

    return [
      {
        label: translatedCategoryTitle,
        value: category.title || "",
        isCategory: true,
        isInitial: isInitialCategory
      },
      ...category.options.map((helpfulOption) => ({
        label: translatedHelpfulOptionsMap.get(helpfulOption) || null,
        value: helpfulOption,
        isCategory: false,
        isInitial: isInitialCategory
      }))
    ]
  })

  return { flatCategorizedOptions: flattenedMostHelpfulOptions }
}
